import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { setIsLoading, setReportFundList } from "../../slices/reportSlice";
import { generateRandomString } from "utils/helper";
import * as services from "../../services/reportApi";
import dayjs from "dayjs";

function useFundReport() {
    const { i18n } = useTranslation();
    const dispatch = useDispatch();
    const currentCompany = useSelector((state) => state.company.currentCompany);
    const reportFundList = useSelector((state) => state.report.reportFund || []);

    const [pagination, setPagination] = useState({ page: 0, currentPage: 0, size: 10 });
    const [search, setSearch] = useState({});
    const [showReport, setShowReport] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isExcelLoading, setIsExcelLoading] = useState(false);
    const [isPDFLoading, setIsPDFLoading] = useState(false);
    const [fundNameRaw, setFundNameRaw] = useState([]);

    // Content-Disposition looks like: attachment; filename="PVD_Fund_Report_202601.xlsx"
    const getFilenameFromHeaders = (res, fallback) => {
        const raw = res?.headers?.["content-disposition"] || res?.headers?.["Content-Disposition"];
        if (!raw) return fallback;
        const match = raw.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
        return match?.[1] ? decodeURIComponent(match[1]) : fallback;
    };

    const fundNameOptions = useMemo(() => {
        return fundNameRaw.map((opt) => ({
            // value MUST be fundNameTh — the transaction's embedded fundInfo only stores
            // name/nameEn (a snapshot at payroll-calc time), there is no fundCode reference.
            value: opt?.fundNameTh,
            label: i18n.language === "en" ? opt?.fundNameEn : opt?.fundNameTh,
        }));
    }, [fundNameRaw, i18n.language]);

    const showModal = () => {
        setIsModalOpen(true);
    };

    const getFundNameOptions = useCallback(
        async (fundType) => {
            setFundNameRaw([]);
            if (!fundType || !currentCompany) return;
            try {
                dispatch(setIsLoading(true));
                const response = await services.getFundNameOptions({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    fundType,
                });
                setFundNameRaw(response?.data || []);
            } catch (error) {
                console.error("Error fetching fund name options:", error);
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, dispatch]
    );

    function toParams(params = {}) {
        const { pagination = {}, sortBy = [] } = params;
        return {
            pagination: {
                page: pagination?.page,
                size: pagination?.pageSize,
            },
            sortBy: sortBy
                .filter((s) => s.order !== undefined)
                .map((s) => ({
                    direction: s.order === "descend" ? "desc" : "asc",
                    property: s.field,
                })),
        };
    }

    const getFundReportTable = useCallback(
        async (params = {}) => {
            try {
                dispatch(setIsLoading(true));
                const response = await services.getFundReportPagination({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    ...params,
                    ...params.pagination,
                });

                dispatch(setReportFundList(response?.data?.page?.content));

                setPagination((prev) => ({
                    ...prev,
                    current: response?.data?.page?.pageable?.pageNumber + 1,
                    pageNo: response?.data?.page?.pageable?.pageNumber + 1,
                    size: response?.data?.page?.pageable?.pageSize,
                    total: response?.data?.page?.totalElements,
                }));
            } catch (error) {
                console.error("Error fetching fund report:", error);
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, dispatch]
    );

    const onChange = useCallback(
        (tablePagination, tableFilters, tableSorter) => {
            const modifiedSorter = Array.isArray(tableSorter) ? [...tableSorter] : [tableSorter];
            const modifiedParams = toParams({
                pagination: {
                    page: tablePagination.current - 1,
                    pageSize: tablePagination.pageSize,
                },
                sortBy: modifiedSorter,
            });
            setPagination((prev) => ({ ...prev, ...modifiedParams?.pagination }));
            getFundReportTable({ ...modifiedParams, ...search });
        },
        [getFundReportTable, search]
    );

    const handleSearch = (values) => {
        setShowReport(true);
        try {
            const searchValues = {
                fundType: values.fundType,
                // values.fundName holds an array of Fund.fundNameTh (multi-select) —
                // matches what's actually embedded in the transaction's fundInfo.name.
                fundNameList: values.fundName?.length ? values.fundName : undefined,
                employeeNo: values.employeeNo?.trim() || undefined,
                employeeFullName: values.employeeFullName?.trim() || undefined,
                year: String(values.year?.year() || dayjs().year()),
                month: values.month?.month() + 1,
            };

            setSearch(searchValues);
            setPagination((prev) => ({ ...prev, page: 0, currentPage: 0 }));

            getFundReportTable({
                ...searchValues,
                pagination: { page: 0, size: pagination.size },
            });
        } catch (error) {
            console.error("Error building fund report search:", error);
        }
    };

    const exportFundReportExcel = useCallback(async () => {
        setIsExcelLoading(true);
        try {
            dispatch(setIsLoading(true));
            const res = await services.exportFundReport({
                requestId: generateRandomString(),
                companyId: currentCompany,
                ...search,
                reportType: "XLSX",
            });
            const url = window.URL.createObjectURL(res?.data);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", getFilenameFromHeaders(res, "fundReport.xlsx"));
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error exporting fund report (xlsx):", error);
        } finally {
            dispatch(setIsLoading(false));
            setIsExcelLoading(false);
        }
    }, [currentCompany, dispatch, search]);

    const exportFundReportPdf = useCallback(async () => {
        setIsPDFLoading(true);
        try {
            dispatch(setIsLoading(true));
            const res = await services.exportFundReport({
                requestId: generateRandomString(),
                companyId: currentCompany,
                ...search,
                reportType: "PDF",
            });
            const url = window.URL.createObjectURL(res?.data);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", getFilenameFromHeaders(res, "fundReport.pdf"));
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error exporting fund report (pdf):", error);
        } finally {
            dispatch(setIsLoading(false));
            setIsPDFLoading(false);
        }
    }, [currentCompany, dispatch, search]);

    return {
        showReport,
        pagination,
        reportFundList,
        fundNameOptions,
        isModalOpen,
        isExcelLoading,
        isPDFLoading,
        handleSearch,
        onChange,
        showModal,
        setIsModalOpen,
        exportFundReportExcel,
        exportFundReportPdf,
        getFundNameOptions,
    };
}

export default useFundReport;